/**
 * Componentes del module kind `assignment` (F3-B):
 *   • AssignmentModuleEditor — autoría (título/instrucciones/tipo/máximo/tipo de
 *     entrega/modo grupo + editor de rúbrica simple: criterios × niveles).
 *   • AssignmentModulePlayer — vista del alumno: instrucciones, entrega
 *     (texto/archivo), y nota + feedback cuando el profesor la devuelve.
 *
 * Local-first: leen/escriben la fila `assignment` (autoría) y
 * `assignment_submission`/`assignment_grade` (consumo) sincronizadas; el
 * write-path aplica authz y sella el flag `late` server-side.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { CheckCircle2, Clock, Paperclip, Plus, Trash2 } from "lucide-solid";
import {
  ASSIGNMENT_KINDS,
  type AssignmentKind,
  gradingScheme,
  isAssignmentKind,
  isSubmissionType,
  normalizeRubric,
  type Rubric,
  type RubricCriterion,
  SUBMISSION_TYPES,
  submitAssignmentMutation,
  updateAssignmentMutation,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { showToast } from "@/components/ui/toast";
import {
  useAssignment,
  useMyActiveCohortForCourse,
  useMyGrades,
  useMySubmission,
} from "../hooks/use-assessment";
import { uploadSubmissionFile } from "../assessment/api";
import { ResubmitBox, SubmissionHistory } from "../tutor/submission-thread";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import type { ModuleComponentProps } from "./registry";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

const KIND_LABELS: Record<AssignmentKind, MessageDescriptor> = {
  homework: msg`Homework`,
  exam: msg`Exam`,
  project: msg`Project`,
  participation: msg`Participation`,
};

// ─── Autoría ───────────────────────────────────────────────────────────────────

export const AssignmentModuleEditor: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const row = useAssignment(() => props.refId ?? undefined);
  const rubric = createMemo<Rubric>(
    () => normalizeRubric(safeParse(row.data?.rubric)) ?? { criteria: [] },
  );

  const save = (patch: Parameters<typeof updateAssignmentMutation>[2]) => {
    const db = drizzle();
    const id = props.refId;
    if (db && id) {
      void updateAssignmentMutation(db, id, patch);
    }
  };
  const saveRubric = (next: Rubric) => save({ rubric: next.criteria.length > 0 ? next : null });

  const addCriterion = () => {
    const next = { criteria: [...rubric().criteria, { name: "", levels: [] }] };
    saveRubric(next);
  };
  const updateCriterion = (idx: number, patch: Partial<RubricCriterion>) => {
    const criteria = rubric().criteria.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    saveRubric({ criteria });
  };
  const removeCriterion = (idx: number) => {
    saveRubric({ criteria: rubric().criteria.filter((_, i) => i !== idx) });
  };
  const addLevel = (ci: number) => {
    const c = rubric().criteria[ci];
    if (!c) {
      return;
    }
    updateCriterion(ci, { levels: [...c.levels, { label: "", points: 0 }] });
  };
  const updateLevel = (ci: number, li: number, label: string, points: number) => {
    const c = rubric().criteria[ci];
    if (!c) {
      return;
    }
    const levels = c.levels.map((l, i) => (i === li ? { label, points } : l));
    updateCriterion(ci, { levels });
  };
  const removeLevel = (ci: number, li: number) => {
    const c = rubric().criteria[ci];
    if (!c) {
      return;
    }
    updateCriterion(ci, { levels: c.levels.filter((_, i) => i !== li) });
  };

  return (
    <Show when={row.data} fallback={<p class="text-xs text-muted-foreground">…</p>}>
      {(a) => (
        <div class="space-y-2 rounded-lg bg-muted/30 p-3" data-testid="assignment-editor">
          <input
            class={field}
            data-testid="edit-assignment-title"
            value={a().title ?? ""}
            onChange={(e) => save({ title: e.currentTarget.value })}
            placeholder={t(msg`Assignment title`)}
          />
          <textarea
            class={field}
            data-testid="edit-assignment-instructions"
            rows={2}
            value={a().instructions ?? ""}
            onChange={(e) => save({ instructions: e.currentTarget.value })}
            placeholder={t(msg`Instructions for your students`)}
          />
          <div class="flex flex-wrap gap-2">
            <select
              class={field}
              style={{ "max-width": "10rem" }}
              data-testid="edit-assignment-kind"
              value={a().kind ?? "homework"}
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (isAssignmentKind(v)) {
                  save({ kind: v });
                }
              }}
            >
              <For each={ASSIGNMENT_KINDS}>
                {(k) => <option value={k}>{t(KIND_LABELS[k])}</option>}
              </For>
            </select>
            <select
              class={field}
              style={{ "max-width": "9rem" }}
              data-testid="edit-assignment-submission-type"
              value={a().submission_type ?? "text"}
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (isSubmissionType(v)) {
                  save({ submission_type: v });
                }
              }}
            >
              <For each={SUBMISSION_TYPES}>{(s) => <option value={s}>{s}</option>}</For>
            </select>
            <input
              type="number"
              class={field}
              style={{ "max-width": "7rem" }}
              data-testid="edit-assignment-max-score"
              value={a().max_score ?? 100}
              min={0}
              onChange={(e) => save({ max_score: Number(e.currentTarget.value) || 0 })}
              placeholder={t(msg`Max score`)}
            />
            <label class="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                data-testid="edit-assignment-group-mode"
                checked={Boolean(a().group_mode)}
                onChange={(e) => save({ group_mode: e.currentTarget.checked })}
              />
              {t(msg`Group`)}
            </label>
          </div>

          {/* Editor de rúbrica */}
          <div class="rounded-lg border border-border p-2" data-testid="rubric-editor">
            <div class="mb-1 flex items-center justify-between">
              <span class="text-xs font-bold uppercase text-muted-foreground">
                {t(msg`Rubric`)}
              </span>
              <button
                type="button"
                class="inline-flex items-center gap-1 text-xs text-accent"
                data-testid="rubric-add-criterion"
                onClick={addCriterion}
              >
                <Plus class="h-3 w-3" /> {t(msg`Criterion`)}
              </button>
            </div>
            <For each={rubric().criteria}>
              {(c, ci) => (
                <div class="mb-2 space-y-1 rounded bg-surface p-2" data-testid="rubric-criterion">
                  <div class="flex gap-1">
                    <input
                      class={field}
                      data-testid="rubric-criterion-name"
                      value={c.name}
                      onChange={(e) => updateCriterion(ci(), { name: e.currentTarget.value })}
                      placeholder={t(msg`Criterion name`)}
                    />
                    <button
                      type="button"
                      data-testid="rubric-remove-criterion"
                      onClick={() => removeCriterion(ci())}
                      class="text-muted-foreground hover:text-danger"
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                  <For each={c.levels}>
                    {(l, li) => (
                      <div class="flex gap-1 pl-3">
                        <input
                          class={field}
                          data-testid="rubric-level-label"
                          value={l.label}
                          onChange={(e) => updateLevel(ci(), li(), e.currentTarget.value, l.points)}
                          placeholder={t(msg`Level`)}
                        />
                        <input
                          type="number"
                          class={field}
                          style={{ "max-width": "5rem" }}
                          data-testid="rubric-level-points"
                          value={l.points}
                          onChange={(e) =>
                            updateLevel(ci(), li(), l.label, Number(e.currentTarget.value) || 0)
                          }
                        />
                        <button
                          type="button"
                          onClick={() => removeLevel(ci(), li())}
                          class="text-muted-foreground hover:text-danger"
                        >
                          <Trash2 class="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </For>
                  <button
                    type="button"
                    class="pl-3 text-xs text-accent"
                    data-testid="rubric-add-level"
                    onClick={() => addLevel(ci())}
                  >
                    + {t(msg`Level`)}
                  </button>
                </div>
              )}
            </For>
          </div>
        </div>
      )}
    </Show>
  );
};

// ─── Player (alumno) ────────────────────────────────────────────────────────────

export const AssignmentModulePlayer: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const assignment = useAssignment(() => props.refId ?? undefined);
  const ctx = useMyActiveCohortForCourse(userId, () => props.courseId);
  const cohortId = createMemo(() => ctx.data?.cohort_id ?? undefined);
  const submission = useMySubmission(
    () => props.refId ?? undefined,
    () => cohortId(),
    userId,
  );
  const grades = useMyGrades(userId);
  const grade = createMemo(() =>
    (grades.data ?? []).find((g) => g.assignment_id === props.refId && g.cohort_id === cohortId()),
  );
  // M3: la vertical de tutorías gatea el historial de versiones + reentrega.
  const tutoringFlag = useFeatureEnabled("academy.tutoring", {
    orgId: () => ctx.data?.organization_id ?? undefined,
  });
  const tutoringOn = () => tutoringFlag().enabled;

  const files = createMemo<string[]>(() => {
    try {
      const raw = submission.data?.files;
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  });

  const saveText = (body: string) => {
    const db = drizzle();
    const a = assignment.data;
    const cid = cohortId();
    const org = ctx.data?.organization_id;
    const uid = userId();
    if (db && a && cid && org && uid) {
      void submitAssignmentMutation(db, {
        assignmentId: a.id,
        cohortId: cid,
        organizationId: org,
        userId: uid,
        body,
        files: files(),
      });
    }
  };

  const onFile = async (fileList: FileList | null) => {
    const a = assignment.data;
    const cid = cohortId();
    const uid = userId();
    const f = fileList?.[0];
    if (!a || !cid || !uid || !f) {
      return;
    }
    try {
      await uploadSubmissionFile(a.id, uid, cid, f);
      showToast(t(msg`File uploaded`), "success");
    } catch {
      showToast(t(msg`Upload failed`), "error");
    }
  };

  return (
    <Show
      when={assignment.data}
      fallback={<p class="text-sm text-muted-foreground">{t(msg`Assignment not synced yet.`)}</p>}
    >
      {(a) => (
        <div
          class="space-y-3 rounded-2xl border border-border bg-surface p-4"
          data-testid="assignment-player"
        >
          <div class="flex items-center gap-2 font-semibold">
            <span data-testid="assignment-player-title">{a().title}</span>
            <span class="rounded-full bg-accent/12 px-2 py-0.5 text-[11px] font-bold text-accent">
              {a().kind}
            </span>
          </div>
          <Show when={a().instructions}>
            <p class="whitespace-pre-wrap text-sm text-muted-foreground">{a().instructions}</p>
          </Show>

          {/* Nota devuelta */}
          <Show when={grade() && (grade()!.status === "graded" || grade()!.status === "returned")}>
            <div class="rounded-xl bg-joy-mint/15 p-3" data-testid="assignment-grade">
              <div class="flex items-center gap-1.5 text-sm font-bold text-joy-mint">
                <CheckCircle2 class="h-4 w-4" />
                {t(msg`Grade`)}: {formatScore(grade()!.score, a().max_score)}
              </div>
              <Show when={grade()!.feedback}>
                <p class="mt-1 whitespace-pre-wrap text-sm" data-testid="assignment-feedback">
                  {grade()!.feedback}
                </p>
              </Show>
            </div>
          </Show>

          {/* Entrega */}
          <Show
            when={cohortId()}
            fallback={
              <p class="text-xs text-muted-foreground">{t(msg`Enroll in a cohort to submit.`)}</p>
            }
          >
            <Show when={submission.data?.late}>
              <div
                class="flex items-center gap-1 text-xs font-bold text-danger"
                data-testid="submission-late"
              >
                <Clock class="h-3 w-3" /> {t(msg`Submitted late`)}
              </div>
            </Show>
            <Show when={a().submission_type === "text"}>
              <textarea
                class={field}
                rows={4}
                data-testid="assignment-submit-text"
                value={submission.data?.body ?? ""}
                onChange={(e) => saveText(e.currentTarget.value)}
                placeholder={t(msg`Write your answer…`)}
              />
            </Show>
            <Show when={a().submission_type === "file"}>
              <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground">
                <Paperclip class="h-4 w-4" />
                {t(msg`Upload file`)}
                <input
                  type="file"
                  class="hidden"
                  data-testid="assignment-submit-file"
                  onChange={(e) => void onFile(e.currentTarget.files)}
                />
              </label>
              <Show when={files().length > 0}>
                <ul class="text-xs text-muted-foreground" data-testid="assignment-files">
                  <For each={files()}>{(k) => <li>{k}</li>}</For>
                </ul>
              </Show>
            </Show>
          </Show>

          {/* M3 · Tutorías: historial de versiones + reentrega tras devolución. */}
          <Show when={cohortId() && ctx.data?.organization_id && tutoringOn()}>
            <div class="space-y-3 border-t border-border pt-3" data-testid="assignment-tutoring">
              <p class="text-sm font-semibold">{t(msg`Submission history`)}</p>
              <SubmissionHistory
                assignmentId={a().id}
                cohortId={cohortId() ?? ""}
                userId={userId() ?? ""}
              />
              <ResubmitBox
                assignmentId={a().id}
                cohortId={cohortId() ?? ""}
                organizationId={ctx.data?.organization_id ?? ""}
                userId={userId() ?? ""}
              />
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
};

function safeParse(raw: string | null | undefined): unknown {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatScore(score: number | null | undefined, max: number | null | undefined): string {
  if (score == null) {
    return "—";
  }
  const raw = max && max > 0 ? score / max : 0;
  return `${score}/${max ?? 0} · ${gradingScheme("ve-0-20").toDisplay(raw)}`;
}
